import jsPDF from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import SalesDealsApi from "api/admins/sales-deals-api";

// IMPORTANT: use bundler-resolved asset, same as OpsCreateInvoiceScreen
import InvoiceLogo from "assets/img/bnk-icon-black.png";

jsPDF.API.svg = function (svgElement, options) {
  return svg2pdf(svgElement, this, options);
};

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // required for canvas/jsPDF
    img.onload = () => resolve(img);
    img.onerror = err => reject(err);
    img.src = url;
  });
}

function formatAmountForDisplay(amount) {
  return new Intl.NumberFormat("id-ID").format(amount);
}

async function buildInvoiceDoc(deal, { displayHeaderAndFooter = false } = {}) {
  const displayCustomerName = true;
  const displayCustomerAddress = true;
  const displayCustomerPhone = true;
  const displayPrices = true;

  const customerName = deal?.user?.detail?.name ?? "";
  const isSelfPickup = deal?.shipment?.method_type === "self_pick_up";
  const customerAddress = isSelfPickup
  ? ""
  : deal?.shipment?.destination?.address ?? "";
  const customerPhone = deal?.shipment?.recipient?.phone_number ?? "";
  const referenceNumber = deal?.reference_number ?? "";
  const formattedInvoiceDate = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const clientAdvisor = deal?.sales_name_in_invoice || "N/A";
  const shippedVia = deal?.shipment?.courier_detail?.logistic?.name || "N/A";
  const downPayment = deal?.down_payment || 0;
  const footerAddress = "id"; // or "sg"

  const invoiceItemList = (deal?.cart?.cart_items || []).map(item => ({
    mainImageUrl: item?.product?.collect_invoice_photos?.comes_with_photo,
    stampImageUrl: item?.product?.collect_invoice_photos?.stamp_photo,
    title: [
      item?.product?.summary_index?.condition?.toUpperCase(),
      item?.product?.name,
      item?.product?.stamp,
    ].filter(Boolean).join(" "),
    description: item?.product?.comes_with || "",
    quantity: 1,
    currency: "IDR",
    amount: parseInt(item?.price, 10) || 0,
  }));

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a5",
  });

  const response = await fetch("/invoice-checkmark.svg");
  const svgText = await response.text();
  const parser = new DOMParser();
  const svgElement = parser.parseFromString(svgText, "image/svg+xml").documentElement;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  let yPos = 70;
  for (const item of invoiceItemList) {
    if (item.mainImageUrl) yPos -= 10;
    if (item.stampImageUrl) yPos -= 10;
  }

  // HEADER LOGO — EXACTLY like OpsCreateInvoiceScreen (conditional)
  if (displayHeaderAndFooter) {
    const logoImg = await loadImage(InvoiceLogo);

    const logoWidth = 50;  // matches ~188px preview width
    const logoHeight = 13;

    doc.addImage(
      logoImg,
      "PNG",
      (pageWidth - logoWidth) / 2,
      yPos - logoHeight - 2,
      logoWidth,
      logoHeight
    );

    yPos += 20;
  }

  // Title - INVOICE
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("INVOICE", margin, yPos);
  yPos += 8;

  // Two-column layout
  const leftColX = margin;
  const rightColX = pageWidth / 2 + 15;
  const startYPos = yPos;

  doc.setFontSize(6.5);

  let leftYPos = startYPos;
  const headerLeftXOffset = 23;

  if (displayCustomerName) {
    doc.setFont("helvetica", "bold");
    doc.text("SOLD TO:", leftColX, leftYPos);
    doc.text(customerName, leftColX + headerLeftXOffset, leftYPos);
    leftYPos += 5;
  }

  if (displayCustomerAddress) {
    doc.setFont("helvetica", "bold");
    doc.text("ADDRESS:", leftColX, leftYPos);
    const addressLines = doc.splitTextToSize(customerAddress, 40);
    doc.text(addressLines, leftColX + headerLeftXOffset, leftYPos);
    leftYPos += addressLines.length * 4;
  }

  if (displayCustomerPhone) {
    doc.setFont("helvetica", "bold");
    doc.text("PHONE NUMBER:", leftColX, leftYPos);
    doc.text(customerPhone, leftColX + headerLeftXOffset, leftYPos);
    leftYPos += 5;
  }

  let rightYPos = startYPos;
  const headerRightXOffset = 23;

  doc.setFont("helvetica", "bold");
  doc.text("NO:", rightColX, rightYPos);
  doc.text(referenceNumber, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("DATE:", rightColX, rightYPos);
  doc.text(formattedInvoiceDate, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("CLIENT ADVISOR:", rightColX, rightYPos);
  doc.text(clientAdvisor, rightColX + headerRightXOffset, rightYPos);
  rightYPos += 5;

  doc.text("SHIPPED VIA:", rightColX, rightYPos);
  doc.text(shippedVia, rightColX + headerRightXOffset, rightYPos);

  yPos = Math.max(leftYPos, rightYPos) + 5;

  // TABLE
  doc.setFontSize(5);
  const descColX = leftColX;
  const qtyColX = pageWidth - margin - 41;
  const amountColX = pageWidth - margin - 31;

  doc.setFont("helvetica", "bold");
  doc.text("DESCRIPTION", descColX, yPos);
  if (displayPrices) {
    doc.text("QTY", qtyColX, yPos);
    doc.text("AMOUNT", amountColX, yPos);
  }
  yPos += 2;

  const lineExtraLength = 4;
  doc.line(leftColX - lineExtraLength, yPos, pageWidth - margin + lineExtraLength, yPos);
  yPos += 4;

  doc.setFont("helvetica", "normal");

  for (const item of invoiceItemList) {
    const itemStartY = yPos;
    let imageSectionHeight = 0;
    const mainImageHeight = 20;

    if (item.mainImageUrl) {
      const img = await loadImage(item.mainImageUrl);
      doc.addImage(img, "JPEG", descColX, itemStartY, (mainImageHeight * 3) / 4, mainImageHeight);
      imageSectionHeight += mainImageHeight;
    }

    if (item.stampImageUrl) {
      const img = await loadImage(item.stampImageUrl);
      doc.addImage(img, "JPEG", descColX, itemStartY + mainImageHeight + 2, (mainImageHeight * 3) / 4, mainImageHeight);
      imageSectionHeight += mainImageHeight + 2;
    }

    const itemTextOffset = 20;
    doc.setFont("helvetica", "bold");
    const titleLines = doc.splitTextToSize(item.title, 40);
    doc.text(titleLines, descColX + itemTextOffset, yPos);
    yPos += titleLines.length * 3;

    doc.setFont("helvetica", "normal");
    const descLines = item.description.split(", ").filter(Boolean);
    for (const line of descLines) {
      await doc.svg(svgElement, {
        x: descColX + itemTextOffset,
        y: yPos - 2,
        width: 2.5,
        height: 2.5,
      });
      doc.text(line, descColX + itemTextOffset + 3.5, yPos);
      yPos += 3;
    }

    if (displayPrices) {
      doc.text(item.quantity.toString(), qtyColX, itemStartY);
      doc.text(`${item.currency} ${formatAmountForDisplay(item.amount)}`, amountColX, itemStartY);
    }

    yPos = Math.max(yPos, itemStartY + imageSectionHeight) + 2;
  }

  if (downPayment > 0 && displayPrices) {
    doc.setFont("helvetica", "bold");
    doc.text("DP", qtyColX, yPos);
    doc.text(`IDR ${formatAmountForDisplay(downPayment)}`, amountColX, yPos);
    yPos += 5;
  }

  if (displayPrices) {
    doc.setFont("helvetica", "bold");
    doc.text("Total", qtyColX, yPos);
    const total = invoiceItemList.reduce((s, it) => s + it.amount, 0) - downPayment;
    doc.text(`IDR ${formatAmountForDisplay(total)}`, amountColX, yPos);
    yPos += 4;
  }

  doc.line(leftColX - lineExtraLength, yPos, pageWidth - margin + lineExtraLength, yPos);
  yPos += 4;

  doc.setFont("helvetica", "normal");
  const disclaimerLines = doc.splitTextToSize(
    "*Semua barang promo (harga khusus atau special price) yang sudah dibeli di BELLE&KATE Room adalah final. Kami tidak menerima klaim pengembalian dana atau pertukaran barang setelah barang dipesan. Terima kasih!",
    pageWidth - margin * 2
  );
  doc.text(disclaimerLines, leftColX, yPos);
  yPos += disclaimerLines.length * 2;

  // FOOTER
  if (displayHeaderAndFooter) {
    yPos = pageHeight - 20;
    doc.setFontSize(5);
    doc.setFont("helvetica", "bold");

    if (footerAddress === "sg") {
      doc.text("313@Somerset", pageWidth / 2, yPos, { align: "center" });
      doc.text("313 Orchard Rd, Level B1 Number 16, Singapore 238895", pageWidth / 2, yPos + 3, { align: "center" });
    } else {
      doc.text("JL. JEND. SUDIRMAN KAV 52-53. LOT 22, RT 5 / RW 3, SENAYAN.", pageWidth / 2, yPos, { align: "center" });
      doc.text("KBY. BARU, KOTA JAKARTA SELATAN, DAERAH KHUSUS IBUKOTA JAKARTA 12190", pageWidth / 2, yPos + 3, { align: "center" });
    }
  }

  return doc;
}

export async function printInvoice(e, deal) {
  e?.preventDefault?.();
  const doc = await buildInvoiceDoc(deal, { displayHeaderAndFooter: false });
  doc.save("invoice.pdf");
}

export async function emailInvoice(e, deal) {
  e?.preventDefault?.();
  const doc = await buildInvoiceDoc(deal, { displayHeaderAndFooter: true });

  let pdfBlob;
  try {
    pdfBlob = doc.output("blob");
  } catch {
    pdfBlob = new Blob([doc.output("arraybuffer")], { type: "application/pdf" });
  }

  const formData = new FormData();
  formData.append("invoice", pdfBlob, `invoice-${deal.reference_number}.pdf`);
  formData.append("to", deal?.user?.email || "");

  const res = await SalesDealsApi.sendInvoiceEmail({
    dealId: deal.id,
    formData,
  });

  return res.data;
}
